import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Clock, ExternalLink, Linkedin, MessageCircle } from 'lucide-react';
import { professionalData } from '../professionalData';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer 
      style={{
        background: 'linear-gradient(135deg, rgba(138, 154, 91, 0.95) 0%, rgba(30, 58, 138, 0.95) 100%)',
        color: 'white',
        padding: '4rem 2rem 2rem'
      }}
      data-testid="footer"
    >
      <div className="container-wide">
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
          gap: '2.5rem',
          marginBottom: '3rem'
        }}>
          {/* Column 1: Brand */}
          <div>
            <h3 style={{ 
              fontFamily: 'var(--font-heading)', 
              fontSize: '1.5rem',
              fontWeight: 700,
              marginBottom: '1rem',
              color: 'white'
            }}>
              Isha Rastogi
            </h3>
            <p style={{ 
              fontSize: '0.875rem', 
              lineHeight: 1.6,
              opacity: 0.9
            }}>
              {professionalData.title}
            </p>
            
            {/* Instagram Button */}
            <div style={{
              marginTop: '2rem'
            }}>
              <p style={{
                fontSize: '0.75rem',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                marginBottom: '0.75rem',
                opacity: 0.9
              }}>
                Follow on Instagram
              </p>
              <a
                href="https://www.instagram.com/isharastogipsychology/"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.75rem 1.25rem',
                  background: 'rgba(255, 255, 255, 0.2)',
                  border: '1px solid rgba(255, 255, 255, 0.4)',
                  borderRadius: '8px',
                  color: 'white',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  textDecoration: 'none',
                  transition: 'all 0.3s ease'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
                Follow @isharastogipsychology
                <ExternalLink size={14} />
              </a>
            </div>
          </div>

          {/* Column 2: Quick Links */}
          <div>
            <h4 style={{ 
              fontSize: '1rem', 
              fontWeight: 600, 
              marginBottom: '1.5rem',
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              color: 'white',
              fontFamily: 'var(--font-body)'
            }}>
              Quick Links
            </h4>
            <ul style={{ 
              listStyle: 'none', 
              padding: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: '0.75rem'
            }}>
              {[
                { path: '/', label: 'Home' },
                { path: '/about', label: 'About' },
                { path: '/research', label: 'Research' },
                { path: '/services', label: 'Services' },
                { path: '/insights', label: 'Insights' },
                { path: '/contact', label: 'Contact' }
              ].map((link) => (
                <li key={link.path}>
                  <Link 
                    to={link.path}
                    style={{
                      color: 'white',
                      fontSize: '0.95rem',
                      opacity: 0.9,
                      textDecoration: 'none',
                      border: 'none',
                      transition: 'opacity 0.2s ease'
                    }}
                    onMouseOver={(e) => { e.target.style.opacity = '1'; e.target.style.border = 'none'; }}
                    onMouseOut={(e) => { e.target.style.opacity = '0.9'; e.target.style.border = 'none'; }}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
            
            {/* Professional Links */}
            <div style={{ marginTop: '2rem' }}>
              <h4 style={{ 
                fontSize: '0.875rem', 
                fontWeight: 600, 
                marginBottom: '1rem',
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                color: 'white'
              }}>
                Professional
              </h4>
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: '0.75rem' 
              }}>
                <a 
                  href="https://www.linkedin.com/in/isha-rastogi/"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ 
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    color: 'white', 
                    textDecoration: 'none', 
                    fontSize: '0.875rem',
                    opacity: 0.9,
                    border: 'none'
                  }}
                  onMouseOver={(e) => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.border = 'none'; }}
                  onMouseOut={(e) => { e.currentTarget.style.opacity = '0.9'; e.currentTarget.style.border = 'none'; }}
                >
                  <Linkedin size={16} />
                  LinkedIn
                </a>
                <a 
                  href={`https://orcid.org/${professionalData.research.orcid}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ 
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    color: 'white', 
                    textDecoration: 'none', 
                    fontSize: '0.875rem',
                    opacity: 0.9,
                    border: 'none'
                  }}
                  onMouseOver={(e) => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.border = 'none'; }}
                  onMouseOut={(e) => { e.currentTarget.style.opacity = '0.9'; e.currentTarget.style.border = 'none'; }}
                >
                  <ExternalLink size={16} />
                  ORCID
                </a>
                <a 
                  href="https://www.researchgate.net/profile/Isha-Rastogi"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ 
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    color: 'white', 
                    textDecoration: 'none', 
                    fontSize: '0.875rem',
                    opacity: 0.9,
                    border: 'none'
                  }}
                  onMouseOver={(e) => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.border = 'none'; }}
                  onMouseOut={(e) => { e.currentTarget.style.opacity = '0.9'; e.currentTarget.style.border = 'none'; }}
                >
                  <ExternalLink size={16} />
                  ResearchGate
                </a>
                
                {/* Modern Minds Prominent Button */}
                <a 
                  href={professionalData.clinic.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ 
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem',
                    background: '#B87333',
                    color: 'white',
                    textDecoration: 'none', 
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    padding: '0.75rem 1.25rem',
                    borderRadius: '8px',
                    border: 'none',
                    marginTop: '0.5rem',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseOver={(e) => { 
                    e.currentTarget.style.background = '#A66429'; 
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.border = 'none'; 
                  }}
                  onMouseOut={(e) => { 
                    e.currentTarget.style.background = '#B87333'; 
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.border = 'none'; 
                  }}
                >
                  Visit Modern Minds Clinic
                  <ExternalLink size={14} />
                </a>
              </div>
            </div>
          </div>

          {/* Column 3: Clinical Info */}
          <div>
            <h4 style={{ 
              fontSize: '1rem', 
              fontWeight: 600, 
              marginBottom: '1.5rem',
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              color: 'white',
              fontFamily: 'var(--font-body)'
            }}>
              Reach Out
            </h4>
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: '1.25rem' 
            }}>
              <a 
                href={`https://www.google.com/maps/search/?api=1&query=1/4A,Vineet+Khand,Gomti+Nagar,Lucknow,UP+226010`}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '0.75rem',
                  fontSize: '0.95rem',
                  opacity: 0.9,
                  color: 'white',
                  textDecoration: 'none',
                  transition: 'opacity 0.2s ease',
                  border: 'none',
                  lineHeight: 1.6
                }}
                onMouseOver={(e) => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.border = 'none'; }}
                onMouseOut={(e) => { e.currentTarget.style.opacity = '0.9'; e.currentTarget.style.border = 'none'; }}
              >
                <MapPin size={18} style={{ marginTop: '2px', flexShrink: 0 }} />
                <span>1/4 A, Vineet Khand, Gomti Nagar, Lucknow, UP 226010, India</span>
              </a>
              <a 
                href="tel:+919569170611"
                style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  color: 'white', 
                  textDecoration: 'none', 
                  fontSize: '0.95rem',
                  opacity: 0.9,
                  border: 'none'
                }}
                onMouseOver={(e) => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.border = 'none'; }}
                onMouseOut={(e) => { e.currentTarget.style.opacity = '0.9'; e.currentTarget.style.border = 'none'; }}
              >
                <Phone size={18} />
                +91 95691 70611
              </a>
              <a 
                href="mailto:isha@modernminds.co.in"
                style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  color: 'white', 
                  textDecoration: 'none', 
                  fontSize: '0.95rem',
                  opacity: 0.9,
                  border: 'none'
                }}
                onMouseOver={(e) => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.border = 'none'; }}
                onMouseOut={(e) => { e.currentTarget.style.opacity = '0.9'; e.currentTarget.style.border = 'none'; }}
              >
                <Mail size={18} />
                isha@modernminds.co.in
              </a>
              <a 
                href={`https://wa.me/919569170611`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  color: 'white', 
                  textDecoration: 'none', 
                  fontSize: '0.95rem',
                  opacity: 0.9,
                  border: 'none'
                }}
                onMouseOver={(e) => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.border = 'none'; }}
                onMouseOut={(e) => { e.currentTarget.style.opacity = '0.9'; e.currentTarget.style.border = 'none'; }}
              >
                <MessageCircle size={18} />
                WhatsApp
              </a>
            </div>
          </div>

          {/* Column 4: Clinical Hours */}
          <div>
            <h4 style={{ 
              fontSize: '1rem', 
              fontWeight: 600, 
              marginBottom: '1.5rem',
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              color: 'white',
              fontFamily: 'var(--font-body)'
            }}>
              Clinical Hours
            </h4>
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: '1.25rem',
              opacity: 0.9
            }}>
              <div style={{ 
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.75rem',
                fontSize: '0.95rem'
              }}>
                <Clock size={18} style={{ marginTop: '2px', flexShrink: 0 }} />
                <div>
                  <p style={{ marginBottom: '0.25rem', fontWeight: 600 }}>Monday – Friday</p>
                  <p style={{ fontSize: '0.875rem', opacity: 0.9 }}>10 AM to 8 PM</p>
                </div>
              </div>
              <div style={{ 
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.75rem',
                fontSize: '0.95rem'
              }}>
                <Clock size={18} style={{ marginTop: '2px', flexShrink: 0 }} />
                <div>
                  <p style={{ marginBottom: '0.25rem', fontWeight: 600 }}>Saturday & Sunday</p>
                  <p style={{ fontSize: '0.875rem', opacity: 0.9 }}>Online Consultations</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div style={{
          paddingTop: '2rem',
          borderTop: '1px solid rgba(255, 255, 255, 0.2)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '0.75rem'
        }}>
          <p style={{ 
            fontSize: '0.875rem', 
            opacity: 0.8,
            textAlign: 'center'
          }}>
            © {currentYear} Isha Rastogi | {professionalData.title}. All rights reserved.
          </p>
          <p style={{ 
            fontSize: '0.875rem', 
            opacity: 0.9,
            fontWeight: 500,
            letterSpacing: '0.05em',
            textAlign: 'center'
          }}>
            Objective Neuro-Psychology & Behavioral Engineering.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
